import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import type {
	AddRemixHistoryEntryPayload,
	AddRemixHistoryStackEntryPayload,
	UpdateRemixImageHistoryFieldIdPayload,
	RemixHistoryEntry,
} from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';

type RemixHistoryStackEntry = RemixHistoryEntry['stack'][number];

function getMatchingStackIndex(
	stack: RemixHistoryStackEntry[],
	{ fieldId, url }: { fieldId?: string; url?: string },
): number {
	if (!fieldId && !url) {
		return -1;
	}

	return stack.findIndex(
		(stackEntry) => (url && stackEntry.url === url) || (fieldId && stackEntry.fieldId === fieldId),
	);
}

export type RemixHistoryPluginState = {
	/**
	 * The history map is keyed by conversationId and stores every remix result for that conversation.
	 * The active stack entry is derived from the editor node URL/fieldId so editor undo/redo can move
	 * through older entries without mutating this plugin state.
	 */
	historyMap: Record<string, RemixHistoryEntry>;
};

export type RemixHistoryPluginMeta =
	| {
			payload: AddRemixHistoryEntryPayload;
			type: 'addHistoryEntry';
	  }
	| {
			payload: UpdateRemixImageHistoryFieldIdPayload;
			type: 'updateRemixImageHistoryFieldId';
	  }
	| {
			payload: AddRemixHistoryStackEntryPayload;
			type: 'addHistoryStackEntry';
	  }
	| {
			type: 'goBackInHistory';
	  }
	| {
			type: 'goForwardInHistory';
	  };

function reducer(
	state: RemixHistoryPluginState,
	meta?: RemixHistoryPluginMeta,
): RemixHistoryPluginState {
	switch (meta?.type) {
		// for image, the fieldId will be updated after the image is uploaded via updateRemixImageHistoryFieldId
		case 'addHistoryEntry': {
			const {
				conversationId,
				dynamicUiSource,
				dynamicUiSubtype,
				dynamicUiType,
				finalMessage,
				url,
				fieldId,
			} = meta.payload;
			const historyEntry: RemixHistoryEntry = {
				conversationId,
				dynamicUiSource,
				latestFinalMessage: finalMessage,
				stack: [{ dynamicUiType, dynamicUiSubtype, finalMessage, url, fieldId }],
			};

			return {
				historyMap: {
					...state.historyMap,
					[conversationId]: historyEntry,
				},
			};
		}

		case 'updateRemixImageHistoryFieldId': {
			const { conversationId, fieldId } = meta.payload;
			const historyEntry = state.historyMap[conversationId];
			if (!historyEntry) {
				return state;
			}

			const currentStackIndex = historyEntry.stack.length - 1;
			if (currentStackIndex < 0) {
				return state;
			}

			const updatedHistoryEntry = {
				...historyEntry,
				stack: historyEntry.stack.map((stackEntry, index) =>
					index === currentStackIndex ? { ...stackEntry, fieldId } : stackEntry,
				),
			};
			return {
				historyMap: {
					...state.historyMap,
					[conversationId]: updatedHistoryEntry,
				},
			};
		}

		case 'addHistoryStackEntry': {
			const {
				conversationId,
				dynamicUiType,
				dynamicUiSubtype,
				finalMessage,
				url,
				fieldId,
				previousUrl,
				previousFieldId,
			} = meta.payload;
			const historyEntry = state.historyMap[conversationId];
			if (!historyEntry) {
				return state;
			}

			const currentStackIndex = getMatchingStackIndex(historyEntry.stack, {
				url: previousUrl,
				fieldId: previousFieldId,
			});
			const stackEntriesToKeep =
				currentStackIndex === -1
					? historyEntry.stack
					: historyEntry.stack.slice(0, currentStackIndex + 1);

			const updatedHistoryEntry: RemixHistoryEntry = {
				...historyEntry,
				latestFinalMessage: finalMessage,
				stack: [
					...stackEntriesToKeep,
					{ dynamicUiType, dynamicUiSubtype, finalMessage, url, fieldId },
				],
			};

			return {
				historyMap: {
					...state.historyMap,
					[conversationId]: updatedHistoryEntry,
				},
			};
		}

		case 'goBackInHistory':
		case 'goForwardInHistory':
		default:
			return state;
	}
}

export const remixHistoryPluginKey: PluginKey<RemixHistoryPluginState> =
	new PluginKey<RemixHistoryPluginState>('remixHistoryPlugin');

export function createRemixHistoryPmPlugin({
	api,
	dispatch,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	dispatch: Dispatch;
}): SafePlugin {
	return new SafePlugin({
		key: remixHistoryPluginKey,

		state: {
			init(): RemixHistoryPluginState {
				return {
					historyMap: {},
				};
			},

			apply(tr, pluginState, _oldState, newState): RemixHistoryPluginState {
				let newPluginState = pluginState;

				const meta = tr.getMeta(remixHistoryPluginKey);
				if (meta) {
					newPluginState = reducer(newPluginState, meta);
				}

				if (newPluginState !== pluginState) {
					dispatch(remixHistoryPluginKey, newPluginState);
				}

				return newPluginState;
			},
		},
	});
}
